import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { TableUtility } from '@utility/dynamic-table';
import { useAuth } from '../../../Hooks/useAuth';
import {
  deleteBillingReportRow,
  getBillingReportBaseURL,
  saveBillingReport,
} from '../../../Api/request/billingReport';
import { toast } from 'react-toastify';
import './BillingReport.scss';
import {
  BillingReportAddDialog,
  BillingReportApproverDialog,
} from './BillingReportDialogs';
import {
  BillingReportActions,
  BillingReportEmptyState,
  BillingReportEditDialog,
  BillingReportFooter,
  getNumericValue,
  BillingReportToolbarControls,
  BillingReportToolbarHeading,
  BillingReportUploadDialog,
} from './BillingReportEditComponents';
import {
  formatIndianAmount,
  getBillingReportPermissions,
  getSelectedProjectAndDate,
  isOneMonthBillingPeriod,
} from './billingReportUtils';
import useBillingReportUpload from './billingReportUpload';

const createCheckbox = ({ checked = false, disabled = false, indeterminate = false, label, onChange }) => (
  <input
    type="checkbox"
    className="billing-report-row-checkbox"
    aria-label={label}
    defaultChecked={checked}
    disabled={disabled}
    ref={(checkbox) => {
      if (checkbox) checkbox.indeterminate = indeterminate;
    }}
    onClick={(event) => event.stopPropagation()}
    onChange={onChange}
  />
);

const syncBillingReportSelectAll = (checkbox) => {
  const table = checkbox.closest('.p-datatable');
  if (!table) return;
  const checkboxes = [...table.querySelectorAll('.billing-report-row-checkbox')];
  const selectAll = checkboxes.find((item) => item.getAttribute('aria-label')
    === 'Select all billing report rows');
  const rowCheckboxes = checkboxes.filter((item) => item !== selectAll);
  if (!selectAll || !rowCheckboxes.length) return;
  const selectedCount = rowCheckboxes.filter((item) => item.checked).length;
  selectAll.checked = selectedCount === rowCheckboxes.length;
  selectAll.indeterminate = selectedCount > 0 && selectedCount < rowCheckboxes.length;
};

const buildTotalRow = (rows, canViewAmount) => {
  const amountRows = rows.filter((row) => row.totalAmountInr !== null
    && row.totalAmountInr !== undefined
    && row.totalAmountInr !== '');
  const totalAmountInr = amountRows.length
    ? amountRows.reduce((sum, row) => sum + getNumericValue(row.totalAmountInr), 0)
    : null;
  return {
    employeeId: 'Total',
    employeeName: '',
    loggedEffort: rows.reduce((sum, row) => sum + getNumericValue(row.loggedEffort), 0),
    billedEffort: rows.reduce((sum, row) => sum + getNumericValue(row.billedEffort), 0),
    totalAmountInr: !canViewAmount || totalAmountInr === null
      ? null : formatIndianAmount(totalAmountInr),
    billingRowSelect: '',
    action: '',
    isTotalRow: true,
    rowConfiguration: {
      isFrozen: true,
      backgroundColor: '#eceff5',
    },
  };
};

/* eslint-disable max-lines-per-function */
const BillingReport = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const permissions = useMemo(
    () => getBillingReportPermissions(userProfile?.permissions || {}),
    [userProfile?.permissions],
  );
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [hasSubmittableRows, setHasSubmittableRows] = useState(false);
  const [isReportLocked, setIsReportLocked] = useState(false);
  const [appliedProjectAndDate, setAppliedProjectAndDate] = useState({
    projectName: '',
    dateRange: '',
    projectId: '',
    periodStartDate: '',
    periodEndDate: '',
  });
  const [tableScrollHeight, setTableScrollHeight] = useState('430px');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isApproverDialogOpen, setIsApproverDialogOpen] = useState(false);
  const [selectedApprover, setSelectedApprover] = useState(null);
  const [editingRows, setEditingRows] = useState([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [deletingRowId, setDeletingRowId] = useState(null);
  const exportRowsRef = useRef([]);
  const tableStateHandlerRef = useRef(null);
  const baseURL = getBillingReportBaseURL();
  const userInfo = useMemo(() => ({
    id: userProfile?.id || '',
    employeeId: userProfile?.employeeId || userProfile?.emp_id || '',
    email: userProfile?.email || '',
    emailId: userProfile?.email || '',
    name: userProfile?.name || userProfile?.employeeName || '',
    permissions: userProfile?.permissions || {},
    roleDetails: userProfile?.roleDetails || [],
  }), [userProfile]);

  const handleFilterUpdate = useCallback(({
    action,
    filters = [],
    tableConfiguration = [],
  }) => {
    const projectFilter = filters.find((filter) => filter.field === 'projectName');
    const dateFilter = filters.find((filter) => filter.field === 'date');
    const hasProject = Boolean(projectFilter?.selectedValue)
      || (Array.isArray(projectFilter?.selectedValues)
        && projectFilter.selectedValues.length > 0);
    const hasDateRange = Boolean(Array.isArray(dateFilter?.range)
      && dateFilter.range.length === 2
      && dateFilter.range[0]
      && dateFilter.range[1]);
    const nextHasAppliedFilters = action === 'apply' && hasProject && hasDateRange;
    setIsReportLocked(false);
    setHasSubmittableRows(false);
    if (nextHasAppliedFilters && !isOneMonthBillingPeriod(dateFilter.range[0], dateFilter.range[1])) {
      toast.error('Select a complete one-month billing period.');
      setHasAppliedFilters(false);
      setSelectedEmployeeIds([]);
      exportRowsRef.current = [];
      setAppliedProjectAndDate({
        projectName: '', dateRange: '', projectId: '', periodStartDate: '', periodEndDate: '',
      });
      return;
    }
    setHasAppliedFilters(nextHasAppliedFilters);
    setSelectedEmployeeIds([]);
    exportRowsRef.current = [];
    setAppliedProjectAndDate(nextHasAppliedFilters
      ? getSelectedProjectAndDate(filters, tableConfiguration)
      : { projectName: '', dateRange: '', projectId: '', periodStartDate: '', periodEndDate: '' });
  }, []);

  const handleDeleteBillingEffort = useCallback(async (row) => {
    if (!permissions.canDelete) {
      toast.error('You do not have permission to delete billing effort rows.');
      return;
    }
    const billingRowId = Number(row?.billingRowId);
    if (!row?.canDelete || !Number.isInteger(billingRowId) || billingRowId < 1) {
      toast.error('Only newly added employees with zero logged effort can be deleted.');
      return;
    }
    if (!window.confirm(`Delete billing report row for ${row.employeeName || row.employeeId}?`)) return;

    setDeletingRowId(billingRowId);
    try {
      const response = await deleteBillingReportRow(billingRowId, { userInfo });
      if (!response?.data?.success) {
        throw new Error(response?.data?.message || 'Unable to delete billing report row.');
      }
      setSelectedEmployeeIds((currentIds) => currentIds.filter((id) => id !== row.employeeId));
      const tableState = tableStateHandlerRef.current?.();
      await tableState?.refreshTableData?.();
      toast.success(response.data.message || 'Billing report row deleted.');
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Unable to delete billing report row.');
    } finally {
      setDeletingRowId(null);
    }
  }, [permissions.canDelete, userInfo]);

  const handleFilteredApiResponse = useCallback((response) => {
    const dataContainer = response?.data?.data && typeof response.data.data === 'object'
      ? response.data.data
      : response?.data?.tableData ? response.data : response;
    const sourceRows = Array.isArray(dataContainer?.tableData) ? dataContainer.tableData : [];
    const reportSubmitted = Boolean(dataContainer?.isSubmitted);
    setIsReportLocked(reportSubmitted);
    const dataRows = sourceRows.filter((row) => !row?.isTotalRow)
      .map((row) => ({ ...row }));
    setHasSubmittableRows(dataRows.some((row) => row.billingRowId && !row.approvalHistoryId));
    setTableScrollHeight(`${Math.min(42 + ((dataRows.length + 1) * 46), 430)}px`);
    exportRowsRef.current = dataRows;
    const tableData = dataRows.map((row) => {
      const employeeId = `${row.employeeId || ''}`;
      const isSelected = selectedEmployeeIds.includes(employeeId);
      return {
        ...row,
        totalAmountInr: formatIndianAmount(row.totalAmountInr),
        action: (
          <BillingReportActions
            disabled={reportSubmitted || row.isLocked
              || deletingRowId === Number(row.billingRowId)}
            canEdit={permissions.canUpdate}
            canDelete={permissions.canDelete}
            deleting={deletingRowId === Number(row.billingRowId)}
            onDelete={handleDeleteBillingEffort}
            onEdit={(selectedRow) => setEditingRows([selectedRow])}
            row={row}
          />
        ),
        billingRowSelect: createCheckbox({
          checked: isSelected,
          disabled: Boolean(row.isLocked) || !permissions.canUpdate,
          label: `Select ${row.employeeName || employeeId}`,
          onChange: (event) => {
            setSelectedEmployeeIds((currentIds) => (event.target.checked
              ? [...new Set([...currentIds, employeeId])]
              : currentIds.filter((id) => id !== employeeId)));
            syncBillingReportSelectAll(event.target);
          },
        }),
      };
    });
    const editableRows = dataRows.filter((row) => !row.isLocked);
    const allRowsSelected = editableRows.length > 0
      && editableRows.every((row) => selectedEmployeeIds.includes(`${row.employeeId || ''}`));
    const selectedRowCount = editableRows.filter((row) => (
      selectedEmployeeIds.includes(`${row.employeeId || ''}`)
    )).length;
    const headerCheckbox = createCheckbox({
      checked: allRowsSelected,
      disabled: !permissions.canUpdate,
      indeterminate: selectedRowCount > 0 && !allRowsSelected,
      label: 'Select all billing report rows',
      onChange: (event) => {
        const table = event.target.closest('.p-datatable');
        table?.querySelectorAll('.billing-report-row-checkbox')
          .forEach((checkbox) => {
            if (checkbox !== event.target && !checkbox.disabled) {
              checkbox.checked = event.target.checked;
            }
          });
        event.target.indeterminate = false;
        setSelectedEmployeeIds(event.target.checked
          ? editableRows.map((row) => `${row.employeeId || ''}`).filter(Boolean)
          : []);
      },
    });

    if (Array.isArray(dataContainer?.tableConfiguration)) {
      const baseColumns = dataContainer.tableConfiguration
        .filter((column) => column.field !== 'billingRowSelect'
          && column.field !== 'action'
          && (permissions.canViewAmount || column.field !== 'totalAmountInr'))
        .map((column) => ({
          ...column,
          align: column.numeric ? 'right' : 'left',
          pinnable: false,
          pinVisible: false,
          showPinButtonInHeader: false,
        }));
      const selectionColumn = {
        field: 'billingRowSelect',
        header: headerCheckbox,
        visible: true,
        filter: false,
        sortable: false,
        pinnable: false,
        pinVisible: false,
        showPinButtonInHeader: false,
        align: 'center',
      };
      const actionColumn = {
        field: 'action',
        header: 'Action',
        visible: true,
        filter: false,
        sortable: false,
        pinnable: false,
        pinVisible: false,
        showPinButtonInHeader: false,
        align: 'center',
      };
      dataContainer.tableConfiguration = [selectionColumn, ...baseColumns, actionColumn];
      if (response.data?.data && typeof response.data.data === 'object') {
        response.data.data.tableConfiguration = dataContainer.tableConfiguration;
      }
      response.tableConfiguration = dataContainer.tableConfiguration;
    }

    const totalRow = tableData.length ? buildTotalRow(tableData, permissions.canViewAmount) : null;
    const tableDataWithTotal = totalRow ? [...tableData, totalRow] : tableData;
    dataContainer.tableData = tableDataWithTotal;
    dataContainer.totalRow = totalRow;
    if (response.data?.data && typeof response.data.data === 'object') {
      response.data.data.tableData = tableDataWithTotal;
      response.data.data.totalRow = totalRow;
    }
    response.tableData = tableDataWithTotal;
    return response;
  }, [deletingRowId, handleDeleteBillingEffort, permissions.canDelete,
    permissions.canUpdate, permissions.canViewAmount, selectedEmployeeIds]);

  const refreshBillingReportTable = useCallback(async () => {
    const tableState = tableStateHandlerRef.current?.();
    if (typeof tableState?.refreshTableData === 'function') {
      await tableState.refreshTableData();
    }
  }, []);

  const billingReportUpload = useBillingReportUpload({
    appliedProjectAndDate,
    canAccessAmount: permissions.canViewAmount
      || permissions.canAddAmount || permissions.canEditAmount,
    refreshTable: refreshBillingReportTable,
    userInfo,
  });

  const handleAddBillingEffort = useCallback(async (row) => {
    if (!permissions.canCreate) {
      toast.error('You do not have permission to add billing effort rows.');
      return false;
    }
    try {
      const response = await saveBillingReport({
        projectId: appliedProjectAndDate.projectId,
        periodStartDate: appliedProjectAndDate.periodStartDate,
        periodEndDate: appliedProjectAndDate.periodEndDate,
        employeeId: row.employeeId,
        billedEffort: row.billedEffort,
        ...(permissions.canAddAmount
          && Object.prototype.hasOwnProperty.call(row, 'totalAmountInr')
          ? { totalAmountInr: row.totalAmountInr } : {}),
        forceZeroLoggedEffort: true,
        userInfo,
      });
      if (!response?.data?.success) {
        throw new Error(response?.data?.message || 'Unable to save billing report');
      }
      setIsAddDialogOpen(false);
      await refreshBillingReportTable();
      toast.success('Billing report saved');
      return true;
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Unable to save billing report');
      return false;
    }
  }, [appliedProjectAndDate, permissions.canAddAmount, permissions.canCreate,
    refreshBillingReportTable, userInfo]);

  const handleEditBillingEffort = useCallback(async (rows) => {
    if (!permissions.canUpdate) {
      toast.error('You do not have permission to edit billing effort rows.');
      return;
    }
    try {
      const responses = await Promise.all(rows.map((row) => saveBillingReport({
        projectId: appliedProjectAndDate.projectId,
        periodStartDate: appliedProjectAndDate.periodStartDate,
        periodEndDate: appliedProjectAndDate.periodEndDate,
        employeeId: row.employeeId,
        billedEffort: row.billedEffort,
        ...(permissions.canEditAmount
          && Object.prototype.hasOwnProperty.call(row, 'totalAmountInr')
          ? { totalAmountInr: row.totalAmountInr } : {}),
        userInfo,
      })));
      const failedResponse = responses.find((response) => !response?.data?.success);
      if (failedResponse) {
        throw new Error(failedResponse?.data?.message || 'Unable to update billing report');
      }
      setEditingRows([]);
      setSelectedEmployeeIds([]);
      await refreshBillingReportTable();
      toast.success(rows.length > 1 ? 'Billing report rows updated' : 'Billing report updated');
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Unable to update billing report');
    }
  }, [appliedProjectAndDate, permissions.canEditAmount, permissions.canUpdate,
    refreshBillingReportTable, userInfo]);

  const handleBulkEdit = useCallback(() => {
    if (!permissions.canUpdate) return;
    const selectedIds = new Set(selectedEmployeeIds);
    const rows = exportRowsRef.current.filter((row) => (
      selectedIds.has(`${row.employeeId}`) && !row.isLocked));
    if (rows.length) setEditingRows(rows);
  }, [permissions.canUpdate, selectedEmployeeIds]);
  const editableSelectedCount = permissions.canUpdate
    ? exportRowsRef.current
      .filter((row) => !row.isLocked && selectedEmployeeIds.includes(`${row.employeeId}`)).length
    : 0;

  const handleSubmitBillingReport = useCallback(() => {
    if (!permissions.canCreate) {
      toast.error('You do not have permission to submit billing effort for approval.');
      return;
    }
    if (hasSubmittableRows) setIsApproverDialogOpen(true);
  }, [hasSubmittableRows, permissions.canCreate]);
  const handleApproverSubmitted = useCallback(async (approver) => {
    setSelectedApprover(approver);
    setIsApproverDialogOpen(false);
    setIsReportLocked(true);
    await refreshBillingReportTable();
  }, [refreshBillingReportTable]);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      exportHeaderUnmerge: true,
      baseURL,
      showToolBar: true,
      showPagination: false,
      showTieredMenu: true,
      headers: { token: userProfile?.access_token || '' },
      userInfo,
      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: false },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: '', enabled: false },
        deleteView: { endpoint: '', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => 'Billing_Report',
          sheetName: () => 'Billing Report',
        },
      },
      handler: {
        onFilterUpdate: handleFilterUpdate,
        handleFilteredApiResponse,
        setTableStateHandler: (getTableState) => {
          tableStateHandlerRef.current = getTableState;
        },
      },
      toolBarSettings: {
        showAddNew: false,
        showDownloadExcel: true,
        showColumnCustomize: false,
        showColumnFilterMenu: true,
        showHelp: false,
        showSearch: false,
        showToolBarHeading: true,
        toolBarHeading: (
          <BillingReportToolbarHeading
            projectName={appliedProjectAndDate.projectName}
            dateRange={appliedProjectAndDate.dateRange}
            onProjectClick={() => navigate('/mis/timesheet-mis')}
          />
        ),
        showSaveView: false,
        showReset: false,
        showExpandCollapse: false,
        showFilterOption: true,
        showFilters: true,
        additionalElements: [
          <BillingReportToolbarControls
            key="billing-report-toolbar-controls"
            selectedCount={editableSelectedCount}
            disabled={!hasAppliedFilters || isReportLocked}
            canEdit={permissions.canUpdate}
            canCreate={permissions.canCreate}
            showAmount={permissions.canViewAmount}
            showApprovalHistory={permissions.canViewApprovalHistory}
            onEdit={handleBulkEdit}
            onTimesheet={() => navigate('/mis/timesheet-mis')}
            onApprovalHistory={() => window.open(
              '/mis/billing-report/approval-history', '_blank', 'noopener,noreferrer',
            )}
            onUpload={billingReportUpload.open}
            onAdd={() => setIsAddDialogOpen(true)}
          />,
        ],
        downloadExcelFileName: 'Billing_Report',
        downloadExcelSheetName: 'Billing Report',
      },
    },
    tableSettings: {
      size: 'small',
      pageSize: 10,
      loaderSkeleton: { rows: 10, bodyCells: 2, columns: 7 },
      paginationConfig: { page: 1, limit: 10, rowsPerPageOptions: [] },
      tableScrollHeight,
      showExpandCollapse: false,
    },
  }), [
    appliedProjectAndDate.dateRange,
    appliedProjectAndDate.projectName,
    baseURL,
    handleFilterUpdate,
    handleFilteredApiResponse,
    handleBulkEdit,
    permissions.canCreate,
    permissions.canUpdate,
    permissions.canViewAmount,
    permissions.canViewApprovalHistory,
    navigate,
    hasAppliedFilters,
    isReportLocked,
    setIsAddDialogOpen,
    tableScrollHeight,
    userInfo,
    userProfile?.access_token,
  ]);
  return (
    <div className="billing-report-container">
      <div className="billing-report-table-shell">
        <div className={`billing-report-table-utility ${hasAppliedFilters
          ? 'billing-report-has-data' : 'billing-report-empty'}`}>
          <TableUtility
            configuration={configuration}
            emptyStateComponent={!hasAppliedFilters ? (
              <BillingReportEmptyState />
            ) : undefined}
            showFilterOption
          />
          {hasAppliedFilters && (
            <BillingReportFooter
              onSubmit={handleSubmitBillingReport}
              submitting={false}
              submitted={isReportLocked}
              disabled={!hasSubmittableRows || !permissions.canCreate}
              onCancel={() => {
                setHasAppliedFilters(false);
                setIsReportLocked(false);
                setAppliedProjectAndDate({
                  projectName: '', dateRange: '', projectId: '', periodStartDate: '', periodEndDate: '',
                });
              }}
            />
          )}
          {isAddDialogOpen && (
            <BillingReportAddDialog
              rows={exportRowsRef.current}
              canAddAmount={permissions.canAddAmount}
              onClose={() => setIsAddDialogOpen(false)}
              onSave={handleAddBillingEffort}
            />
          )}
          {editingRows.length > 0 && (
            <BillingReportEditDialog
              rows={editingRows}
              canViewAmount={permissions.canViewAmount}
              canEditAmount={permissions.canEditAmount}
              onClose={() => setEditingRows([])}
              onSave={handleEditBillingEffort}
            />
          )}
          <BillingReportApproverDialog
            open={isApproverDialogOpen}
            selectedApprover={selectedApprover}
            projectId={appliedProjectAndDate.projectId}
            periodStartDate={appliedProjectAndDate.periodStartDate}
            periodEndDate={appliedProjectAndDate.periodEndDate}
            userInfo={userInfo}
            onClose={() => setIsApproverDialogOpen(false)}
            onSave={handleApproverSubmitted}
          />
          {billingReportUpload.isOpen && (
            <BillingReportUploadDialog
              fileName={billingReportUpload.fileName}
              uploading={billingReportUpload.isUploading}
              onClose={billingReportUpload.close}
              onDownloadSample={billingReportUpload.downloadSample}
              onFileChange={billingReportUpload.onFileChange}
              onUpload={billingReportUpload.onUpload}
            />
          )}
        </div>
      </div>
    </div>
  );
};
/* eslint-enable max-lines-per-function */
export default BillingReport;
